/*
 * ////////////////////////////////////////////////////////////////////////////////
 * //
 * // This software system consists of computer software and documentation.
 * // It contains trade secrets and confidential information which are proprietary
 * // to Everi Games Inc.  Its use or disclosure in whole or in part without
 * // the express written permission of Everi Games Inc. is prohibited.
 * //
 * // This software system is also an unpublished work protected under the copyright
 * // laws of the United States of America.
 * //
 * // Copyright © 2018 Everi Games Inc.  All Rights Reserved
 * //
 * ////////////////////////////////////////////////////////////////////////////////
 */
import MoveNodeEffect from '../Effects/MoveNodeEffect';
import GameSetup, { AutoSpinType } from '../Game/GameSetup';
import RecallManager from '../Game/RecallManager';
import StateDependencyComponent from '../StateManager/StateDependencyComponent';
import StateEvent from '../StateManager/StateEvent';
import { StateID } from '../StateManager/StateID';
import StateReference from '../StateManager/StateReference';
import { IToggle } from '../Utility/Component/Action/IToggle';
import NodeToggleActionComponent from '../Utility/Component/Action/NodeToggleActionComponent';
import NodeToggleAnimComponent from '../Utility/Component/Action/NodeToggleAnimComponent';
import NodeToggleSpineComponent from '../Utility/Component/Action/NodeToggleSpineComponent';
import NodeTouchActionComponent from '../Utility/Component/Action/NodeTouchActionComponent';
import InjectableComponent from '../Utility/Component/ComponentReference/InjectableComponent';
import { ReferenceGroup } from '../Utility/Component/ComponentReference/ReferenceGroup';
import { ContextKey } from '../Utility/Component/Configurator/ContextKey';
import ObservableIntComponent from '../Utility/Component/Observable/ObservableIntComponent';
import IPopupManager from '../Utility/Component/Popup/IPopupManager';
import PopupDispatcher from '../Utility/Component/Popup/PopupDispatcher';
import CLog, { LogTypes } from '../Utility/ComponentLogger';
import AutoPlaySetterComponent from './AutoPlaySetterComponent';
import BetManager from './BetManager';
import IPlaybar from './IPlaybar';

/** @hidden */
// tslint:disable-next-line:typedef
const property = cc._decorator.property;

/** Controls the various UI components and behaviour of the playbar. */
@cc._decorator.ccclass
export default class PlaybarManager extends StateDependencyComponent implements IPlaybar
{
    /** On which state should the playbar buttons be enabled, initially. */
    @property({ type: StateReference, visible: true })
    private _enableOnState: StateReference = new StateReference(StateID.WaitForPlay, true);

    /** On which state should the playbar buttons be re-enabled, after having been disabled. */
    @property({ type: [StateReference], visible: true })
    private _reEnableStates: StateReference[] = [new StateReference(StateID.PostPlay, true)];

    /** On which state should the playbar buttons be temporarily disabled. */
    @property({ type: StateReference, visible: true })
    private _disableState: StateReference = new StateReference(StateID.Request, true);

    /** On which state should the auto play panel be configured. */
    @property({ type: StateReference, visible: true })
    private _setupAutoPlayState: StateReference = new StateReference(StateID.CheckRecall, true);

    /** Button component used to display the autoplay menu. */
    @property({ type: cc.Button, visible: true })
    private _autoPlayButton: cc.Button = null;

    @property({ type: cc.Layout, visible: true })
    private _autoPlayOptionContainer: cc.Layout = null;

    @property({ type: MoveNodeEffect, visible: true })
    private _autoPlayOpenEffect: MoveNodeEffect = null;

    @property({ type: cc.Prefab, visible: true })
    private _autoPlayOptionPrefab: cc.Prefab = null;

    @property ({ visible: true })
    private _autoPlayOptionsVertical: boolean = false;

    @property(cc.Button)
    protected playButton: cc.Button = null;

    @property(cc.Button)
    protected noAPPlayButton: cc.Button = null;

    @property(cc.Button)
    protected menuButton: cc.Button = null;

    @property(cc.Node)
    protected playButtonGroup: cc.Node = null;

    @property(cc.Node)
    protected noAPButtonGroup: cc.Node = null;

    @property(cc.Node)
    protected playIcon: cc.Node = null;

    @property(cc.Node)
    protected autoPlayIcon: cc.Node = null;

    /** Which nodes of the UI should act as toggles between cash and currency when clicked. */
    @property({ type: [cc.Node], visible: true })
    private _cashToggles: cc.Node[] = [];

    /** @hidden */
    protected autoPlayObservableRef: InjectableComponent<ObservableIntComponent>
        = new InjectableComponent<ObservableIntComponent>(ReferenceGroup.MainGame, "AutoPlay.Remaining");
    /** @hidden */
    protected betManagerRef: InjectableComponent<BetManager> = new InjectableComponent<BetManager>();
    /** @hidden */
    protected gameSetupRef: InjectableComponent<GameSetup> = new InjectableComponent<GameSetup>();
    /** @hidden */
    protected recallManagerRef: InjectableComponent<RecallManager> = new InjectableComponent<RecallManager>();
    /** @hidden */
    protected popupManagerRef: InjectableComponent<IPopupManager> = new InjectableComponent<IPopupManager>();

    /** Whether the playbar is interacteable. */
    private _isLocked: boolean = false;
    /** Public accessor to whether interacteable. */
    public get IsLocked(): boolean { return this._isLocked; }

    private _activePlayButtonGroup: cc.Node = null;

    /** Components to disable / enable on lock. */
    private _disableComponents: cc.Component[] = [];

    /** @hidden */
    private static readonly MAX_BUTTON_COUNT: number = 5;
    /** @hidden */
    private static readonly MIN_BUTTON_COUNT: number = 4;

    protected standardExcludes: cc.Node[] = [];

    private _autoPlayTopContainer: cc.Layout = null;

    /** @hidden */
    protected setContexts(): void
    {
        this.autoPlayObservableRef.SetContext(ObservableIntComponent);
        this.betManagerRef.SetContext(BetManager);
        this.gameSetupRef.SetContext(GameSetup);
        this.recallManagerRef.SetContext(RecallManager);
        this.popupManagerRef.SetContext(ContextKey.POPUP_MANAGER);
    }

    /** @hidden */
    protected start(): void
    {
        super.start(
            this._enableOnState,
            this._reEnableStates,
            this._disableState,
            this._setupAutoPlayState
        );

        this._activePlayButtonGroup = this.playButtonGroup;
        this._autoPlayTopContainer = this.getFirstParent(cc.Layout, this._autoPlayOptionContainer.node);

        this.configureDependencies();
    }

    /** @hidden */
    protected onStateChangeHandler = (event: StateEvent): void =>
    {
        this._reEnableStates.forEach((state) =>
        {
            if (state.Value === event.State)
            {
                this.setEnabled(true);

                this.betManagerRef.Value.UnLockBetValues();
                this.completeState(event.State);
            }
        }, this);

        switch (event.State)
        {
            case this._enableOnState.Value:
                this.setEnabled(true);
                this.betManagerRef.Value.UnLockBetValues();
                this.completeState(event.State);
                break;
            case this._disableState.Value:
                this.setEnabled(false, this.popupManagerRef.Value && this.popupManagerRef.Value.IsOpen
                    ? [this.menuButton.node]
                    : this.standardExcludes);
                this.betManagerRef.Value.LockBetValues();
                this.completeState(event.State);
                break;
            case this._setupAutoPlayState.Value:
                this.configureAutoPlay();
                this.completeState(event.State);
                break;
        }
    }

    /** @hidden */
    private configureAutoPlay(): void
    {
        if (this.gameSetupRef.Value.AutoSpinType === AutoSpinType.None)
        {
            this._activePlayButtonGroup = this.noAPButtonGroup;
            this.playButtonGroup.active = false;
            this.noAPButtonGroup.active = true;
        }
        else
        {
            let panelToggle: NodeToggleActionComponent = this._autoPlayButton.getComponent(NodeToggleActionComponent);
            let caretToggle: NodeToggleAnimComponent
                = this._autoPlayButton.getComponentInChildren(NodeToggleAnimComponent);

            this.addAutoPlayButtons(this._autoPlayOptionContainer, [panelToggle, caretToggle], true);

            if (this._autoPlayTopContainer) { this._autoPlayTopContainer.updateLayout(); }

            this.autoPlayObservableRef.Value.OnChanged((event: cc.Event.EventCustom) =>
            {
                this.autoPlayIcon.active = this.autoPlayObservableRef.Value.Value > 0;
                this.playIcon.active = this.autoPlayObservableRef.Value.Value === 0;

                if (event.detail < 1 && this._enableOnState.Value.IsCurrentState())
                {
                    this.betManagerRef.Value.UnLockBetValues();
                }
            });
        }
    }

    private addAutoPlayButtons = (
        container: cc.Layout,
        toggles: IToggle[] = [],
        disablePopupManager: boolean = false): void =>
    {
        let asOptions: number[] = this.gameSetupRef.Value.AutoSpinOptions;
        if (!asOptions) { return; }
        let options: number[] = this.gameSetupRef.Value.AutoSpinOptions.slice(0);

        if (!options || options.length === 0) { return; }

        options.push(0);
        options.sort(this._autoPlayOptionsVertical ? (a, b) => b - a : (a, b) => a - b);

        let addedButtonCount: number = 0;
        let optbuttonSize: cc.Rect = null;
        const maxButtonCount: number = PlaybarManager.MAX_BUTTON_COUNT;

        for (let opt of options)
        {
            let optButton: cc.Node = cc.instantiate(this._autoPlayOptionPrefab);
            container.node.addChild(optButton);

            if (!optbuttonSize) { optbuttonSize = optButton.getBoundingBoxToWorld(); }

            let autoPlaySetter: AutoPlaySetterComponent = optButton.getComponentInChildren(AutoPlaySetterComponent);
            autoPlaySetter.AutoPlayAmount = opt;
            autoPlaySetter.DisablePopupManager = disablePopupManager;

            optButton.getComponentInChildren(cc.Label).string = opt === 0 ? '0' : opt.toString(10);
            optButton.getComponentInChildren(cc.Button).node.on(cc.Node.EventType.TOUCH_END, () =>
            {
                toggles.forEach((toggle) => toggle.ToggleOff());
            });

            addedButtonCount++;

            if (addedButtonCount === maxButtonCount) { break; }
        }

        if (options.length < maxButtonCount)
        {
            let diff: number = maxButtonCount - options.length;

            if (options.length < maxButtonCount)
            {
                if (this._autoPlayOptionsVertical)
                {
                    const scaleHeight: number = optbuttonSize.height / 2;
                    let spacing: number = ((scaleHeight + container.spacingY / 2) * diff) - container.spacingY;
                    spacing *= container.node.scaleY;

                    container.paddingBottom = spacing;
                }
                else
                {
                    let spacing: number
                        = ((optbuttonSize.width / 2 + container.spacingX / 2) * diff) - container.spacingX;
                    spacing *= container.node.scaleX;

                    container.paddingLeft = spacing;
                    container.paddingRight = spacing;
                }

                diff = maxButtonCount - PlaybarManager.MIN_BUTTON_COUNT;
            }

            if (!this._autoPlayOptionsVertical) {
                for (let nodeTransform of this._autoPlayOpenEffect.NodeTransforms)
                {
                    nodeTransform.Transform.x
                        += (diff * optbuttonSize.width) + (diff * container.spacingX);
                }
            }
        }

        this.scheduleOnce(() => { container.updateLayout(); }, 0.1);
    }

    /** @hidden */
    private configureDependencies(): void
    {
        this.betManagerRef.WithValue(mgr =>
            {
                for (const node of this._cashToggles)
                {
                    node.on(cc.Node.EventType.TOUCH_END, (event) =>
                    {
                        event.bubbles = false;
                        mgr.ToggleCash();
                    });
                }
            }, () => CLog.warn("No betmanager configured.", LogTypes.Initialization, this));

        this._disableComponents.push(...this.getComponentsInChildren(cc.Button));
        this._disableComponents.push(...this.getComponentsInChildren(PopupDispatcher));
        this._disableComponents.push(...this.getComponentsInChildren(NodeToggleActionComponent));
        this._disableComponents.push(...this.getComponentsInChildren(NodeToggleSpineComponent));
        this._disableComponents.push(...this.getComponentsInChildren(NodeTouchActionComponent));

        this.standardExcludes = [this.playButton.node, this.noAPPlayButton.node, this.menuButton.node];
    }

    public HidePlayButton(): void
    {
        if (this._activePlayButtonGroup) { this._activePlayButtonGroup.active = false; }
    }

    public ShowPlayButton(): void
    {
        if (this._activePlayButtonGroup) { this._activePlayButtonGroup.active = true; }
    }

    /** Enables playbar buttons and other interacteable items. */
    public ToggleOn(): void
    {
        let didEnable: boolean = false;

        if ((this._enableOnState.Value && this._enableOnState.Value.IsCurrentState())
                || this._reEnableStates.some(StateReference.IsCurrentFilter))
        {
            this.setEnabled(true);
            didEnable = true;
        }

        if (!didEnable)
        {
            this.setEnabled(false, this.standardExcludes);
        }
    }

    /** Disables playbar buttons and other interacteable items. */
    public ToggleOff(excludeMenuButton: boolean = false): void
    {
        this.setEnabled(false, excludeMenuButton ? [this.menuButton.node] : []);
    }

    /** Helper for transitioning the playbar between enabled/disabled states. */
    private setEnabled(to: boolean, excludes: cc.Node[] = []): void
    {
        // Playbar should always be disabled during recall.  This is to prevent re-enabling.
        if (this.recallManagerRef.Value.Recalling)
        {
            to = false;
            excludes = [];
        }
        else if (this.popupManagerRef.WithValue(mgr => mgr.IsOpen)) { return; }

        this._disableComponents.forEach((component) =>
        {
            if (excludes.indexOf(component.node) > -1)
            {
                if (component instanceof cc.Button)
                {
                    let button: cc.Button = component as cc.Button;
                    button.interactable = true;
                }

                component.enabled = true;
                return;
            }

            const toggle: IToggle = component as any;
            if (to === this._isLocked && toggle.ToggleOff) { toggle.ToggleOff(); }
            if (component instanceof cc.Button) { (component as cc.Button).interactable = to; }
            component.enabled = to;
        });

        this._isLocked = !to;
    }
}
